// Cross-browser "copy text to clipboard" with an iPad / iOS-safe fallback.
//
// Why this exists: `navigator.clipboard.writeText` is the modern path, but it is
// `undefined` in non-secure (plain `http://`) contexts — which is exactly how
// the app is often reached on an iPad (a LAN `http://<ip>:<port>` URL) — and it
// is unreliable on older iOS Safari. When the async Clipboard API is missing or
// rejects, we fall back to a hidden `<textarea>` + `document.execCommand('copy')`
// using the Range/selection dance iOS requires (a plain `.select()` is ignored
// there).
//
// Call this directly inside the click handler. When the async API is absent the
// fallback runs synchronously (no `await` is reached before `execCommand`), so
// the browser's transient user-activation is preserved and the copy succeeds.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
/** Copy `text` to the clipboard. Resolves `true` on success, `false` otherwise. */
export var copyTextToClipboard = function (text) { return __awaiter(void 0, void 0, void 0, function () {
    var _a;
    var _b;
    return __generator(this, function (_c) {
        switch (_c.label) {
            case 0:
                if (!text)
                    return [2 /*return*/, false];
                if (!((_b = navigator.clipboard) === null || _b === void 0 ? void 0 : _b.writeText)) return [3 /*break*/, 4];
                _c.label = 1;
            case 1:
                _c.trys.push([1, 3, , 4]);
                return [4 /*yield*/, navigator.clipboard.writeText(text)];
            case 2:
                _c.sent();
                return [2 /*return*/, true];
            case 3:
                _a = _c.sent();
                return [3 /*break*/, 4];
            case 4: return [2 /*return*/, legacyCopy(text)];
        }
    });
}); };
/** iOS/iPad + non-secure-context fallback using a hidden textarea. */
var legacyCopy = function (text) {
    var textarea = document.createElement('textarea');
    var selection = null;
    var savedRange = null;
    try {
        textarea.value = text;
        textarea.readOnly = true;
        textarea.setAttribute('aria-hidden', 'true');
        // Keep it visually hidden but selectable; `position: fixed` prevents the
        // page from scrolling to it when it takes focus.
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.width = '1px';
        textarea.style.height = '1px';
        textarea.style.padding = '0';
        textarea.style.border = 'none';
        textarea.style.outline = 'none';
        textarea.style.boxShadow = 'none';
        textarea.style.background = 'transparent';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        // Preserve whatever the user had selected so we can restore it after.
        selection = document.getSelection();
        savedRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        // iPadOS 13+ reports `MacIntel` with touch points, so sniff both.
        var isIOS = /ipad|iphone|ipod/i.test(navigator.userAgent) ||
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        if (isIOS) {
            // iOS needs a Range selection on a contentEditable element plus an
            // explicit setSelectionRange — `.select()` alone does nothing there.
            textarea.contentEditable = 'true';
            var range = document.createRange();
            range.selectNodeContents(textarea);
            selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
            selection === null || selection === void 0 ? void 0 : selection.addRange(range);
            textarea.setSelectionRange(0, text.length);
        }
        else {
            textarea.select();
        }
        return document.execCommand('copy');
    }
    catch (_a) {
        return false;
    }
    finally {
        // Detach the hidden textarea even if `execCommand` threw — otherwise it is
        // orphaned in the DOM, where it stays focusable to a screen reader.
        textarea.remove();
        if (savedRange && selection) {
            selection.removeAllRanges();
            selection.addRange(savedRange);
        }
    }
};
